import { isAzam } from '@ps20-frontend/shop-integration-redux';
import { SearchDialogContainerState } from '@ps20-frontend/gui-components';
import { Feature } from '@ps20/bff-models';
import { App, areAppTypesEquivalent, isVhvV2, isVwgV2 } from '@ps20-frontend/frontend-models';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../localStore';
import { FileDeletion } from '../reducer/transient-state.reducer';
import { FragebogenType } from '../model/fragebogenType.enum';
import { CheckoutPersonenSearchListItem } from '../model/checkoutPersonenSearchListItem';
import { selectedProdukte } from './produkt.selector';
import { isMaklerbetreuerOrMaklerOrUvm, isVertriebstelefonie } from './role.selector';


export const selectTarifierungIsInProgress = (state: IAppState): boolean => {
  return state.transientState.tarifierungIsInProgress;
};

export const selectInfoPaketDruckIsInProgress = (state: IAppState): boolean => {
  return state.transientState.infoPaketDruckIsInProgress;
};

export const selectBeiblattDruckIsInProgress = (state: IAppState): boolean => {
  return state.transientState.beiblattDruckIsInProgress;
};

export const selectPkbDruckIsInProgress = (state: IAppState): boolean => {
  return state.transientState.pkbDruckIsInProgress;
};

export const selectJagdbestaetigungDruckIsInProgress = (state: IAppState): boolean => {
  return state.transientState.jagdbestaetigungDruckIsInProgress;
};

export const selectWeweDruckIsInProgress = (state: IAppState): boolean => {
  return state.transientState.weweDruckIsInProgress;
};

export const selectEinreichenIsInProgress = (state: IAppState): boolean => {
  return state.transientState.einreichenIsInProgress;
};

export const selectTestdokumenteDownloadIsInProgress = (state: IAppState): boolean => {
  return state.transientState.testdokumenteDownloadIsInProgress;
};

export const selectShowWaitingOverlay = (state: IAppState): boolean =>
  selectEinreichenIsInProgress(state) || selectTestdokumenteDownloadIsInProgress(state);

export const selectImmoModalOpened = (state: IAppState): boolean =>
  state.transientState.immoModalOpened;

export const selectOnboardingVideo = (state: IAppState): boolean =>
  state.transientState.onboardingVideo;

// Apps whose presence alone (or combined only with each other) hides the onboarding video checkbox.
// Add a new predicate here when a future product needs the same treatment.
const HIDDEN_ONBOARDING_VIDEO_APP_CHECKS: ((appId: App) => boolean)[] = [
  appId => areAppTypesEquivalent(appId, App.TKV),
  isVwgV2,
  isVhvV2,
];

export const isHiddenOnboardingVideoApp = (appId: App): boolean =>
  HIDDEN_ONBOARDING_VIDEO_APP_CHECKS.some(check => check(appId));

// Temporary: Hide onboarding video checkbox when the cart contains only apps listed
// in HIDDEN_ONBOARDING_VIDEO_APP_CHECKS (e.g. TKV/TKV_V2, VWG_V2, VHV_V2)
export const selectShouldShowOnboardingVideoCheckbox = (state: IAppState): boolean => {
  const produkte = selectedProdukte(state);
  if (produkte.length === 0) {
    return true;
  }

  const hasOnlyHiddenOnboardingVideoApps = produkte.every(produkt =>
    isHiddenOnboardingVideoApp(produkt.appId)
  );

  return !hasOnlyHiddenOnboardingVideoApps;
};

export const selectZurueckZumShopOpen = (state: IAppState): boolean =>
  state.transientState.zurueckZumShopModalOpen;

export const selectShowRabattHinweis = (state: IAppState): boolean =>
  state.transientState.showRabattHinweis;

export const selectValidationTriggered = (state: IAppState): boolean =>
  state.transientState.validationTriggered;

export const isNotAzam = (state: IAppState): boolean => {
  return !isAzam(state.transientState.role);
};

export const selectPersonenSuchdialog = (
  state: IAppState
): SearchDialogContainerState<CheckoutPersonenSearchListItem> =>
  state.transientState.personenSuchdialog;

export const selectIsPermasession = (state: IAppState): boolean =>
  !!state.transientState?.permasession;

export const selectEinreichenSimulieren = (state: IAppState): boolean =>
  !!state.transientState?.einreichenSimulieren;

export const selectShowEinreichenSimulierenCheckbox = (state: IAppState): boolean =>
  selectIsPermasession(state) && selectFeatureToggle(state, Feature.TEST_DATA) === true;

export const isSdwSession = (state: IAppState): boolean =>
  !isNullOrUndefined(state.kdv?.partyId) && !isNullOrUndefined(state.transientState?.accessToken);

export const shouldUseSdwEndpoint = (state: IAppState): boolean =>
  isSdwSession(state) || !isNullOrUndefined(state.transientState?.accessToken);

const isNullOrUndefined = (value: unknown): boolean => [null, undefined].includes(value);

export const selectFileDeletionMessages = (state: IAppState): FileDeletion[] => {
  return state.transientState.fileDeletion;
};

export const selectFileModificationInProgress = (state: IAppState): boolean => {
  return state.transientState.fileModificationInProgress;
};

export const wasFragebogenValidationRevealed = (
  state: IAppState,
  angebotsId: string,
  fragebogenType: FragebogenType,
): boolean =>
  !!state.transientState?.fragebogenValidationRevealed?.some(
    revealed =>
      revealed.angebotsId === angebotsId && revealed.fragebogenType === fragebogenType,
  );

export const selectIsFernabsatzFeatureActive = (state: IAppState): boolean =>
  selectFeatureToggle(state, Feature.FERNABSATZ);

export const selectShowFernabsatz = (state: IAppState): boolean =>
  selectIsFernabsatzFeatureActive(state) && !isMaklerbetreuerOrMaklerOrUvm(state);

export const selectIsFallbackEnableTelefonverkaufForAllFeatureActive = (state: IAppState): boolean => {
  return selectFeatureToggle(state, Feature.FALLBACK_ENABLE_TELEFONVERKAUF_FOR_ALL);
};

export const selectIsTelefonEinheitTelefonverkaufFeatureActive = (state: IAppState): boolean => {
  return selectFeatureToggle(state, Feature.TELEFON_EINHEIT_TELEFONVERKAUF);
};


export const selectIsTelefonverkaufAvailable = (state: IAppState): boolean => {
  const isVertriebstelefonieActive = selectIsTelefonEinheitTelefonverkaufFeatureActive(state) && isVertriebstelefonie(state);
  const enabledForAllByFallback = selectIsFallbackEnableTelefonverkaufForAllFeatureActive(state) && !isMaklerbetreuerOrMaklerOrUvm(state);

  return isVertriebstelefonieActive || enabledForAllByFallback;
};
